/**
 * 清新薄荷模版 - 登录页（v4）
 * 
 * 设计核心理念：与红色模版（40/60左右分栏）完全不同的全屏居中玻璃卡片布局
 * - 全屏 teal 渐变背景 + 装饰元素
 * - 居中半透明玻璃卡片（毛玻璃效果）
 * - 上方悬浮 Logo + 欢迎语，下方表单 + 链接
 * - 更现代、更轻盈的视觉风格
 */

import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSiteConfig } from "@/context/SiteConfigContext";

export default function TealLoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { siteConfig } = useSiteConfig();

  const from = (location.state as { from?: string })?.from || "/";

  const siteLogo = siteConfig?.logo || "https://public.readdy.ai/ai/img_res/1ebe5b90-bc3d-41ad-9f51-693742fb28f5.png";
  const siteDisplayName = siteConfig?.site_name || siteConfig?.name || "商服汇";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!username.trim()) { setError("请输入用户名"); return; }
    if (!password.trim()) { setError("请输入密码"); return; }

    const result = await login(username, password);
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.message || "登录失败");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-700 flex items-center justify-center px-4 py-8">
      {/* ── 装饰性背景元素 ── */}
      {/* 右上角大圆 */}
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
      {/* 左下角大圆 */}
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-teal-300/10 rounded-full blur-3xl" />
      {/* 中间光晕 */}
      <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-emerald-300/10 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-cyan-300/10 rounded-full blur-3xl" />
      
      {/* 网格纹理 */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
      }} />

      {/* ── 主体卡片 ── */}
      <div className="relative z-10 w-full max-w-md">
        {/* 顶部 Logo + 欢迎语 */}
        <div className="text-center mb-8">
          {siteLogo && (
            <Link to="/" className="inline-block mb-5">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center p-2 shadow-lg shadow-black/10">
                <img src={siteLogo} alt={siteDisplayName} className="w-full h-full object-contain brightness-0 invert" />
              </div>
            </Link>
          )}
          <h1 className="text-3xl font-extrabold text-white mb-2">欢迎回来</h1>
          <p className="text-teal-100 text-sm">登录您的 {siteDisplayName} 账户</p>
        </div>

        {/* 玻璃卡片 */}
        <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-8 shadow-2xl shadow-teal-900/20 border border-white/20">
          {/* 返回链接 */}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 hover:text-teal-500 transition-colors mb-6 group"
          >
            <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" />
            返回首页
          </Link>

          {/* 表单 */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 用户名 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">用户名</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-user-3-line text-gray-400 text-lg" />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="请输入用户名"
                  className="w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent focus:bg-white transition-all"
                />
              </div>
            </div>

            {/* 密码 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">密码</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <i className="ri-lock-2-line text-gray-400 text-lg" />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="请输入密码"
                  className="w-full pl-11 pr-12 py-3.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent focus:bg-white transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 cursor-pointer rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"} />
                </button>
              </div>
            </div>

            {/* 记住我 & 忘记密码 */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2.5 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 accent-teal-500 cursor-pointer"
                />
                <span className="text-sm text-gray-500 group-hover:text-gray-700 transition-colors">记住我</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors">
                忘记密码？
              </Link>
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="p-4 bg-teal-50 text-teal-700 text-sm rounded-xl flex items-center gap-3 border border-teal-100">
                <i className="ri-error-warning-line shrink-0 text-lg" />
                <span>{error}</span>
              </div>
            )}

            {/* 登录按钮 */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-gradient-to-r from-teal-600 to-emerald-600 text-white font-semibold rounded-xl hover:from-teal-700 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-teal-500/25 hover:shadow-teal-500/40 text-base"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin" />
                  登录中...
                </span>
              ) : (
                "登  录"
              )}
            </button>
          </form>

          {/* 底部注册链接 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              还没有账户？{" "}
              <Link to="/register" className="text-teal-600 font-semibold hover:text-teal-700 transition-colors underline underline-offset-2">
                立即注册
              </Link>
            </p>
          </div>
        </div>

        {/* 底部版权 */}
        <p className="text-center text-teal-100/60 text-xs mt-8">
          &copy; {new Date().getFullYear()} {siteDisplayName}. All rights reserved.
        </p>
      </div>
    </div>
  );
}
